import styled, { css } from 'styled-components';

import { Select } from '../../../components/Select';
import { Button } from '../../../core';
import { BUTTON_SPEC, ButtonType, buttonFocusRing } from '../../../core/Button/spec';

// The width of the global focus outline, as the core Button keeps it for forced-colors mode.
const FORCED_COLORS_OUTLINE_WIDTH = 2;

/**
 * A core Button held in its hover, active or focus state for the states tables. The colours
 * come from BUTTON_SPEC, the same source the Button's own :hover / :active / :focus-visible
 * rules use, so a preview cannot drift from the real state. `&&` outranks the Button's base
 * rule; `pointer-events: none` keeps a real hover from replacing the held state.
 */
export const StatePreview = styled(Button)<{ $state: 'hover' | 'active' | 'focus' }>(({
  theme,
  variant = 'default',
  $state,
}) => {
  if ($state === 'focus') {
    return css`
      && {
        pointer-events: none;
        outline: ${FORCED_COLORS_OUTLINE_WIDTH}px solid transparent;
        box-shadow: ${buttonFocusRing(theme)};
      }
    `;
  }
  const { background, text, border } = BUTTON_SPEC[variant as ButtonType][$state];

  return css`
    && {
      pointer-events: none;
      background-color: ${background === 'transparent' ? 'transparent' : theme[background]};
      color: ${theme[text]};
      border-color: ${border ? theme[border] : 'transparent'};
    }
  `;
});

export const StatesTableContainer = styled.div`
  overflow-x: auto;

  td {
    vertical-align: middle;
  }

  /* Room for the focus ring, which paints outside the button. */
  td:not(:first-child) {
    padding: 0.75rem;
  }
`;

export const PlaygroundContainer = styled.div(
  ({ theme }) => css`
    width: 100%;
    height: 287px;
    background-color: ${theme.neutralBackgroundMedium};
    padding: ${theme.fontSizes.base};
  `,
);

export const PlaygroundSelectors = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;

  > * {
    flex: 0 0 10.8125rem;
  }
`;

export const SelectionFieldContainer = styled(Select)(
  ({ theme }) => css`
    > .options-container {
      background-color: ${theme.neutralBackgroundBase};
    }
  `,
);
